import { z, type ZodType } from '@customerio/hydra-validation';
import type { CustomForm as UIAPICustomForm } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Custom form (workspace-scoped) — third-party forms (Typeform / Jotform /
 * scanned HTML / API) connected to Customer.io. The integrations directory
 * reads only the form-type + status predicates below.
 */
export type CustomForm = UIAPICustomForm;

/** Form type discriminants (subset the directory reads). */
export const CustomFormType = {
  custom: 'custom',
  typeform: 'typeform',
  jotform: 'jotform',
} as const;

/** Connection status values. */
export const CustomFormStatus = {
  draft: 'draft',
  connected: 'connected',
  disconnected: 'disconnected',
} as const;

const CustomFormFromApi = z.looseObject({
  id: z.string(),
}) as unknown as ZodType<CustomForm, unknown>;

/** @public */
export const customForm = defineEntity<CustomForm, CustomForm, void, undefined, 'workspace'>({
  name: 'custom_form',
  scope: 'workspace',
  pk: (f) => f.id,
  schema: { read: CustomFormFromApi },
});

export const customFormKeys = customForm.keys;

export function parseCustomForm(raw: UIAPICustomForm): CustomForm {
  return customForm.parse(raw);
}

type FormPredicateInput = Pick<CustomForm, 'form_type' | 'status' | 'api_form_id' | 'html_form_inputs'>;

/** `custom-form.isConnectedOrDraft` — a connected or draft form. */
export function isConnectedOrDraftForm(form: Pick<CustomForm, 'status'>): boolean {
  return form.status === CustomFormStatus.connected || form.status === CustomFormStatus.draft;
}

/** `custom-form.isApiForm` — a custom form with an API form id. */
export function isApiForm(form: FormPredicateInput): boolean {
  return form.form_type === CustomFormType.custom && form.api_form_id !== '';
}

/** `custom-form.isScannedForm` — a custom form with scanned HTML inputs. */
export function isScannedForm(form: FormPredicateInput): boolean {
  return form.form_type === CustomFormType.custom && (form.html_form_inputs?.length ?? 0) > 0;
}

/** `custom-form.isTypeform` */
export function isTypeform(form: Pick<CustomForm, 'form_type'>): boolean {
  return form.form_type === CustomFormType.typeform;
}

/** `custom-form.isJotform` */
export function isJotform(form: Pick<CustomForm, 'form_type'>): boolean {
  return form.form_type === CustomFormType.jotform;
}
